import { cn, NoddiIcon } from 'noddi-ui-common';
import { extractUserInitialsFromName, getColorForName, getReadableTextColor } from 'noddi-util';
import { avatarVariants, type AvatarSize } from './avatarVariants';

interface Props {
  name?: string | null;
  size?: AvatarSize;
  className?: string;
  /** When set, overrides the background color derived from the name. */
  color?: string;
  /**
   * `outline` drops the name-derived fill for a hollow chip — for rows that must recede
   * (e.g. a route that has not started yet) without losing the initials.
   */
  variant?: 'filled' | 'outline';
  /**
   * Overrides the initials derived from the name — for a list that tells apart two names with the
   * same initials. The color still follows the name.
   */
  initials?: string;
}

export const WorkerAvatar = ({ name, size = 'sm', className, color, variant = 'filled', initials }: Props) => {
  if (!name) {
    return (
      <div className={cn(avatarVariants({ size, variant, hasName: false }), className)}>
        <NoddiIcon
          name='CircleUserRound'
          size={size === 'xl' ? 'large' : size === 'lg' ? 'medium' : size === 'md' ? 'small' : 'xSmall'}
        />
      </div>
    );
  }

  const classes = cn(avatarVariants({ size, variant }), className);
  const text = initials ?? extractUserInitialsFromName(name);

  if (variant === 'outline') {
    return <div className={classes}>{text}</div>;
  }

  const bgColor = color ?? getColorForName(name);

  return (
    <div className={classes} style={{ backgroundColor: bgColor, color: getReadableTextColor(bgColor) }}>
      {text}
    </div>
  );
};
